/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import classNames from "classnames";

import SwipeableViews from "react-swipeable-views";
import { Typography, Tabs, Tab, Fab, Hidden, Tooltip } from "@material-ui/core";
import { Apps, Favorite } from "@material-ui/icons";

// CT Kit
import Parallax from "components/Parallax/Parallax.jsx";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import { isCtMan } from "client/reducers/userReducer";

import { PostsZone } from "./PostsZone";
import {
  getCurrentCourse,
  getImage,
  ApricotAction,
} from "client/actions/courses";
import { selectorCurrentCourse } from "client/selectors/courseSelector";
import { AllOwns } from "client/reducers/ownReducer";
import { getCurrentOwn } from "client/actions/own";
import { selectorOwns, selectorCurrentOwn } from "client/selectors/ownSelector";
import { CommentDialog } from "client/views/CoursesPage/CommentDialog";

import requireCheck from "client/hocs/requireCheck";
import { renderProgress } from "components/Form/FormField";
import {
  CourseActions,
  getCourseActionProps,
} from "client/views/CoursesPage/CourseActions";

import coursePageStyle from "assets/jss/views/coursePage/coursePageStyle.jsx";
const styles = createStyles(coursePageStyle as any);

interface TabContainerProps {
  children?: React.ReactNode;
  value: Number;
  index: Number;
}

function TabContainer(props: TabContainerProps) {
  const { index, value, children } = props;
  return (
    <Typography component="div" style={{ padding: "4px 0" }}>
      {
        // for 'lazy mount', add value/index checking
        value === index && children
      }
    </Typography>
  );
}

interface Props extends WithStyles<typeof styles> {
  currentCourse: any;
  user: any;
  owns: AllOwns;
  currentOwn: OwnEntry;
  courseImages: {};
  // actions
  getCurrentCourse: ApricotAction;
  getCurrentOwn: ApricotAction;
  getImage: ApricotAction;
  // component props
  match: any;
  classes: any;
  history: any;
}

const CoursePageBase: React.FC<Props> = ({
  getCurrentCourse,
  getCurrentOwn,
  getImage,
  currentCourse,
  user,
  owns,
  currentOwn,
  courseImages,
  match,
  classes,
  history,
}: Props) => {
  const [value, setValue] = useState(0);
  const { cid } = match.params;

  useEffect(() => {
    if (cid) {
      getCurrentCourse(cid);
      getCurrentOwn(user ? user._id : null, cid);
    }
  }, []);

  useEffect(() => {
    if (currentCourse) {
      const { _id, imageLink } = currentCourse;
      if (_id === cid && imageLink && !courseImages[_id]) {
        getImage({ cid: _id, imgPath: imageLink });
      }
    }
  }, [currentCourse]);

  const renderLike = (lid: string) => {
    const { lecData } = currentOwn;
    return (
      lecData &&
      lecData[lid] &&
      lecData[lid].liked && (
        <span className={classes.like}>
          <Favorite />
        </span>
      )
    );
  };

  const renderLectures = () => {
    if (
      !owns ||
      !currentCourse ||
      currentCourse._id !== cid ||
      !currentOwn ||
      (currentOwn.course && currentOwn.course != cid)
    )
      return "";

    const { info } = getCourseActionProps(owns, currentCourse, true);

    var lecCount = 0;
    return currentCourse._id === cid ? (
      <>
        <div style={{ marginTop: "1rem" }}>
          <CourseActions info={info} history={history} />
        </div>
        <Card
          lectures
          background
          style={{
            backgroundImage: courseImages[cid]
              ? `url(data:image/jpeg;base64,${courseImages[cid]})`
              : ``,
          }}>
          <CardBody background>
            <ul>
              {currentCourse.lectures.map((lec: any) => {
                const lecTitle = (
                  <h3 className={classes.title}>
                    {renderLike(lec._id)}
                    <span style={{ fontFamily: "Open Sans" }}>
                      {++lecCount + ". "}
                    </span>
                    <span style={{ color: lec.sched ? "#B0B0B0" : "white" }}>
                      {lec.title}
                    </span>
                  </h3>
                );

                return (
                  <li key={lec._id}>
                    {lec.sched && !isCtMan(user.tc) ? (
                      lecTitle
                    ) : (
                      <Link to={`/clsrm/${currentCourse._id}/${lec._id}`}>
                        {lecTitle}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      </>
    ) : (
      <></>
    );
  };

  const allCoursesButton = () => {
    const fabStyle: any = {
      position: "absolute",
      top: "1rem",
      right: "1rem",
      zIndex: 10,
    };

    return (
      <>
        <Hidden smDown>
          <Fab
            style={fabStyle}
            variant="extended"
            color="primary"
            onClick={() => history.push("/cr_list")}>
            <Apps />
            &nbsp;Browse All Courses
          </Fab>
        </Hidden>
        <Hidden mdUp>
          <Tooltip title={"Browse All Courses"}>
            <Fab
              style={fabStyle}
              color="primary"
              onClick={() => history.push("/cr_list")}>
              <Apps />
            </Fab>
          </Tooltip>
        </Hidden>
      </>
    );
  };

  return (
    <>
      <Parallax
        image={require("assets/img/laptop-coffee.jpg")}
        filter="course"
        banner>
        <div className={classes.container}>
          <GridContainer>
            <GridItem className={classNames(classes.mrAuto)}>
              <div className={classes.brand}>
                {currentCourse && currentCourse._id === cid && (
                  <h2 className={classes.title}>{currentCourse.title}</h2>
                )}
              </div>
            </GridItem>
          </GridContainer>
        </div>
      </Parallax>
      <div className={classes.pageContent} style={{ padding: "30px 0" }}>
        <div className={classes.container}>
          {allCoursesButton()}

          <Tabs
            value={value}
            onChange={(e, newValue) => setValue(newValue)}
            variant="scrollable"
            scrollButtons="on"
            indicatorColor="primary"
            textColor="primary">
            <Tab
              label="Lectures"
              classes={{ root: classes.tabRoot }}
              icon={
                <i
                  className="far fa-list-alt"
                  style={{ width: "30px", height: "30px" }}
                />
              }
            />
            <Tab
              label="Comments"
              classes={{ root: classes.tabRoot }}
              icon={
                <i
                  className="far fa-comments"
                  style={{ width: "30px", height: "30px" }}
                />
              }
            />
          </Tabs>
          <SwipeableViews
            index={value}
            onChangeIndex={(index: number) => setValue(index)}>
            <TabContainer index={0} value={value}>
              {value === 0 &&
                (currentCourse ? renderLectures() : renderProgress())}
            </TabContainer>
            <TabContainer index={1} value={value}>
              {value === 1 && (
                <>
                  <CommentDialog
                    history={history}
                    ytlist={currentCourse.ytlist}
                    onExit={() => setValue(0)}
                  />
                  <PostsZone cid={cid} />
                </>
              )}
            </TabContainer>
          </SwipeableViews>
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    currentCourse: selectorCurrentCourse(state),
    courseImages: state.state_course.courseImages,
    owns: selectorOwns(state),
    currentOwn: selectorCurrentOwn(state),
    user: state.state_auth,
  };
}

function loadData(store: any, params: any) {
  if (params.id != "style.css" && params.id != undefined) {
    // 'style.css' got passed as id for unknown reason...
    return store.dispatch(getCurrentCourse(params.id));
  }
}

const base: any = CoursePageBase;
export const CoursePage = {
  loadData,
  component: requireCheck(
    withStyles(styles)(
      connect(mapStateToProps, { getCurrentCourse, getImage, getCurrentOwn })(
        base,
      ),
    ),
    // if logged in, make sure registered
    { loginRequired: false },
  ),
};

export const CoursePageTest = withStyles(styles)(CoursePageBase);
